/**
 * userApi.ts
 *
 * File-level:
 * - Small helper utilities to fetch user/profile information in a robust, silent way.
 *
 * Purpose:
 * - Try multiple common endpoints for user/profile information while avoiding noisy 404 logs.
 * - Return a normalized profile shape or null when nothing could be resolved.
 */

import { fetchJSON } from '@/utils/api';

/**
 * UserProfile
 *
 * @description Normalized shape returned by fetchUserProfile.
 */
export interface UserProfile {
  /** First name, when available */
  firstName?: string | null;
  /** Last name, when available */
  lastName?: string | null;
  /** Best-effort display name (first + last or displayName) */
  displayName?: string | null;
  /** Profession/occupation when available */
  profession?: string | null;
  /** Original payload (if caller needs extra fields) */
  raw?: any;
}

/**
 * fetchUserProfile
 *
 * @description Try multiple candidate endpoints to resolve a user's profile.
 * - Uses fetchJSON so runtime/api base normalization is applied consistently.
 * - Silently ignores non-OK responses and network errors to avoid polluting the console.
 *
 * @param partnerId string - the user id to fetch
 * @returns Promise<UserProfile | null>
 */
export async function fetchUserProfile(partnerId: string): Promise<UserProfile | null> {
  if (!partnerId) return null;

  // Candidate relative paths (do not start with '/api' — fetchJSON/runtime will handle base)
  const candidates = [
    `users/${encodeURIComponent(partnerId)}`,
    `user/${encodeURIComponent(partnerId)}`,
    `profile/${encodeURIComponent(partnerId)}`,
    `users/basic/${encodeURIComponent(partnerId)}`,
    `users/${encodeURIComponent(partnerId)}/basic`,
    // Add fallback shapes (server-specific)
    `profiles/${encodeURIComponent(partnerId)}`,
  ];

  for (const path of candidates) {
    try {
      const json = await fetchJSON(path);
      if (!json) continue;
      const payload = json.data ?? json.user ?? json.profile ?? json;
      if (!payload) continue;

      const first = payload?.firstName ?? payload?.firstname ?? null;
      const last = payload?.lastName ?? payload?.lastname ?? null;
      const displayName =
        (payload?.displayName ?? payload?.fullName ?? payload?.name) ||
        [first, last].filter(Boolean).join(' ').trim() ||
        null;
      const profession = payload?.profession ?? payload?.occupation ?? null;

      return {
        firstName: first,
        lastName: last,
        displayName: displayName ? String(displayName) : null,
        profession: profession ? String(profession) : null,
        raw: payload,
      };
    } catch (err) {
      // Intentionally silent — try next candidate
      // Do not console.error here to avoid noisy logs from 404 / 500 for candidate endpoints.
    }
  }

  return null;
}